/**
 * URL utility functions
 */

/**
 * Combines base URL with relative URL
 */
export function combineUrls(baseUrl: string, relativeUrl: string): string {
  if (isAbsoluteUrl(relativeUrl)) {
    return relativeUrl
  }

  const base = baseUrl.replace(/\/+$/, '')
  const relative = relativeUrl.replace(/^\/+/, '')

  return `${base}/${relative}`
}

/**
 * Checks if URL is absolute
 */
export function isAbsoluteUrl(url: string): boolean {
  return /^https?:\/\//.test(url)
}

/**
 * Builds URL with query parameters
 */
export function buildUrlWithParams(url: string, params: Record<string, any>): string {
  if (!params || Object.keys(params).length === 0) {
    return url
  }

  const searchParams = new URLSearchParams()

  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined) {
      if (Array.isArray(value)) {
        value.forEach(item => searchParams.append(key, String(item)))
      } else {
        searchParams.append(key, String(value))
      }
    }
  }

  const separator = url.includes('?') ? '&' : '?'
  return url + separator + searchParams.toString()
}

/**
 * Parses query parameters from URL
 */
export function parseUrlParams(url: string): Record<string, string | string[]> {
  const urlObj = new URL(url)
  const params: Record<string, string | string[]> = {}

  for (const [key, value] of urlObj.searchParams.entries()) {
    if (key in params) {
      if (Array.isArray(params[key])) {
        ;(params[key] as string[]).push(value)
      } else {
        params[key] = [params[key] as string, value]
      }
    } else {
      params[key] = value
    }
  }

  return params
}

/**
 * Normalizes URL by removing redundant parts
 */
export function normalizeUrl(url: string): string {
  try {
    const urlObj = new URL(url)

    // Remove default ports
    if (
      (urlObj.protocol === 'http:' && urlObj.port === '80') ||
      (urlObj.protocol === 'https:' && urlObj.port === '443')
    ) {
      urlObj.port = ''
    }

    // Normalize pathname
    urlObj.pathname = urlObj.pathname.replace(/\/+/g, '/').replace(/\/$/, '') || '/'

    return urlObj.toString()
  } catch {
    return url
  }
}

/**
 * Extracts domain from URL
 */
export function extractDomain(url: string): string {
  try {
    return new URL(url).hostname
  } catch {
    return ''
  }
}

/**
 * Checks if two URLs have the same origin
 */
export function isSameOrigin(url1: string, url2: string): boolean {
  try {
    const origin1 = new URL(url1).origin
    const origin2 = new URL(url2).origin
    return origin1 === origin2
  } catch {
    return false
  }
}

/**
 * Converts relative URL to absolute using base URL
 */
export function toAbsoluteUrl(relativeUrl: string, baseUrl: string): string {
  try {
    return new URL(relativeUrl, baseUrl).toString()
  } catch {
    return relativeUrl
  }
}
